// packages/frontend/src/contexts/ToastContext.tsx

import React, { ReactNode } from 'react';
import { ToastProvider, useToast } from '../components/ui/Toast';

// Re-export the new toast system for backward compatibility
export { ToastProvider, useToast } from '../components/ui/Toast';

// Legacy interface for backward compatibility
export interface ToastData {
  id: string;
  variant: 'default' | 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
  showProgress?: boolean;
  confetti?: boolean;
  onClose?: () => void;
}

// Legacy wrapper component for backward compatibility
interface LegacyToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

export const LegacyToastProvider: React.FC<LegacyToastProviderProps> = ({
  children,
}) => {
  return (
    <ToastProvider>
      {children}
    </ToastProvider>
  );
};

// Legacy hook wrapper for backward compatibility
export const useLegacyToast = () => {
  const newToast = useToast();
  
  return {
    toasts: [],
    showToast: (toast: Omit<ToastData, 'id'>) => {
      const { variant, message, duration, onClose } = toast;
      const toastType = variant === 'default' ? 'info' : variant;
      return newToast[toastType](message, { duration, onClose });
    },
    hideToast: (id: string) => newToast.removeToast(id),
    hideAllToasts: () => newToast.clearToasts(),
    showSuccess: (message: string, options: Partial<ToastData> = {}) => 
      newToast.success(message, { duration: options.duration, onClose: options.onClose }),
    showError: (message: string, options: Partial<ToastData> = {}) => 
      newToast.error(message, { duration: options.duration, onClose: options.onClose }),
    showWarning: (message: string, options: Partial<ToastData> = {}) => 
      newToast.warning(message, { duration: options.duration, onClose: options.onClose }),
    showInfo: (message: string, options: Partial<ToastData> = {}) => 
      newToast.info(message, { duration: options.duration, onClose: options.onClose }),
  };
};

// Convenience hooks for specific toast types (legacy)
export const useSuccessToast = () => {
  const { success } = useToast();
  return success;
};

export const useErrorToast = () => {
  const { error } = useToast();
  return error;
};

export const useWarningToast = () => {
  const { warning } = useToast();
  return warning;
};

export const useInfoToast = () => {
  const { info } = useToast();
  return info;
};